import ScrollModal from "@/components/common/scroll-modal";
import TextField from "@/components/common/text-field";
import {
  Button,
  Heading,
  Icon,
  Input,
  Radiobutton,
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import { useState } from "react";
import TextFieldID from "@/pages/digital-files/entity/components/TextFieldID";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import type { EntityField } from "../../types";
import { useConfigModal } from "../hooks/useConfigModal";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import { DataCenterField } from "../components/DataCenterField";
import { Renderify } from "renderify-js";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";

export interface TextFieldConfigModalProps {
  isOpen: boolean;
  isCreateMode?: boolean;
  formData?: EntityField;
  onSave: (values: EntityField) => void;
  onClose: () => void;
}
export const TextFieldConfigModal = ({
  isOpen,
  isCreateMode = true,
  formData,
  onClose,
  onSave,
}: TextFieldConfigModalProps) => {
  const [localListNewItem, setLocalListNewItem] = useState("");

  const {
    fieldKey,
    fieldName,
    fieldNameError,
    defaultValue,
    localListItems,
    hasDropdown,
    buttonPosition,
    valuesSource,
    expandSelectedRecord,
    placeholder,
    maxLength,
    valuesTableName,
    valuesColumnName,
    setFieldKey,
    setFieldName,
    setDefaultValue,
    setLocalListItems,
    setHasDropdown,
    setButtonPosition,
    setValuesSource,
    setExpandSelectedRecord,
    setPlaceholder,
    setMaxLength,
    setDataCenterValue,
    getConfigData,
    isValid,
    isNew,

    isArray,
    setIsArray,
    allowedQuantityOfValues,
    setAllowedQuantityOfValues,
  } = useConfigModal("text", formData);

  const handleSave = () => {
    const values = getConfigData();
    onSave(values);
    onClose();
  };

  const handleAddLocalItem = () => {
    const v = localListNewItem.trim();
    if (!v) return;
    setLocalListItems((prev) => [...prev, v]);
    setLocalListNewItem("");
  };

  const handleRemoveLocalItem = (index: number) => {
    setLocalListItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDataCenterChange = (
    _field: string,
    tableName: string,
    columnName: string,
  ) => {
    setDataCenterValue(tableName, columnName);
  };

  const maxNumberOfValuesMask = maskitoNumberOptionsGenerator({
    min: 1,
    minimumFractionDigits: 0,
    // thousandSeparator: ",",
  });
  const allowedQuantityOfValuesMask = useMaskito({
    options: maxNumberOfValuesMask,
  });

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col gap-0 text-size-body-sm"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Campo de texto</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="px-8 py-3">
          <div className="flex gap-3">
            <Button variant="tertiary" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="default"
              className="flex-1"
              disabled={!isValid}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      }
    >
      <div className="p-8 flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Detalles
          </Heading>

          <TextFieldID
            label="Nombre del campo"
            required
            value={fieldName}
            onChange={setFieldName}
            keyValue={fieldKey}
            onKeyChange={setFieldKey}
            canChangeKey={isCreateMode || isNew}
            error={fieldNameError}
          />
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Opciones
          </Heading>

          <SwitchField
            id="text-field-dropdown"
            label="¿Agregar botón dropdown al campo?"
            required
            checked={hasDropdown}
            onChange={setHasDropdown}
            isTrue="Sí"
            isFalse="No"
          />

          {hasDropdown && (
            <>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-body-loud text-sm leading-2xl">
                  Posición del botón{" "}
                  <span className="text-sm text-danger">*</span>
                </label>
                <DropdownList
                  options={[
                    { value: "left", label: "Izquierda" },
                    { value: "right", label: "Derecha" },
                  ]}
                  placeholder="Seleccionar"
                  selectedValue={{
                    value: buttonPosition,
                    label: buttonPosition === "left" ? "Izquierda" : "Derecha",
                  }}
                  toggleSelection={(item) => {
                    if (item.value === "left" || item.value === "right") {
                      setButtonPosition(item.value);
                    }
                  }}
                  widthClassName="w-full"
                  scrollClassName="h-auto"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="font-semibold text-body-loud text-sm leading-2xl">
                  ¿De donde provienen los valores?{" "}
                  <span className="text-sm text-danger">*</span>
                </div>
                <div className="flex flex-col gap-2 mt-1">
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="text-field-source-database"
                      name="text-field-source"
                      value="database"
                      checked={valuesSource === "database"}
                      onChange={() => setValuesSource("database")}
                    />
                    <label
                      htmlFor="text-field-source-database"
                      className="text-body text-sm"
                    >
                      Desde centro de datos
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="text-field-source-manual"
                      name="text-field-source"
                      value="manual"
                      checked={valuesSource === "manual"}
                      onChange={() => setValuesSource("manual")}
                    />
                    <label
                      htmlFor="text-field-source-manual"
                      className="text-body text-sm"
                    >
                      Locales
                    </label>
                  </div>
                </div>
              </div>

              {valuesSource === "manual" && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end group">
                    <div className="w-full">
                      <TextField
                        placeholder="Añade un nuevo item"
                        className="rounded-tr-none rounded-br-none border-r-0"
                        label="Lista local"
                        required
                        value={localListNewItem}
                        onChange={(e) => setLocalListNewItem(e.target.value)}
                      />
                    </div>
                    <Button
                      className="h-11 rounded-bl-none rounded-tl-none border-l-0 group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                      variant="quiet"
                      type="button"
                      onClick={handleAddLocalItem}
                      aria-label="Agregar item"
                    >
                      <Icon name="plus" type="far" />
                    </Button>
                  </div>

                  <InfoMessage>
                    El primer valor de la lista será el pre-seleccionado por
                    default
                  </InfoMessage>

                  {localListItems.length > 0 && (
                    <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                      <div className="flex flex-col">
                        {localListItems.map((item, index) => (
                          <div
                            key={`${item}-${index}`}
                            className="px-3 py-2 flex items-center gap-3"
                          >
                            <div className="flex-1 text-body text-sm truncate">
                              {item}
                            </div>
                            <Button
                              variant="quiet"
                              size="icon"
                              type="button"
                              aria-label="Eliminar"
                              onClick={() => handleRemoveLocalItem(index)}
                              className="w-8 h-8"
                            >
                              <Icon type="far" name="times" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {valuesSource === "database" && (
                <DataCenterField
                  fieldKey="valueDatacenter"
                  required
                  selectedTable={valuesTableName}
                  selectedColumn={valuesColumnName}
                  onChange={handleDataCenterChange}
                />
              )}
              {valuesSource === "database" && (
                <SwitchField
                  id="text-field-expand-selected"
                  label="Expandir registro seleccionado"
                  required
                  checked={expandSelectedRecord}
                  onChange={setExpandSelectedRecord}
                  isTrue="Sí, expandir"
                  isFalse="No, generar una sola variable (array)"
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Configuración
          </Heading>

          <TextField
            label="Placeholder del campo"
            required
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Escribe un placeholder para el campo"
          />

          <TextField
            label="Número de caracteres permitidos"
            required
            type="number"
            value={maxLength}
            onChange={(e) => setMaxLength(e.target.value)}
          />

          <div>
            <GroupTextField
              label="Valor por defecto"
              labelHelperText="Opcional"
            >
              <GroupTextFieldRow className="items-center">
                <Input
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder="Escribe un valor por defecto"
                  className="w-full"
                />
              </GroupTextFieldRow>
            </GroupTextField>

            <InfoMessage className="mt-1">
              Si está vacío no se aplicará ningún valor por defecto.
            </InfoMessage>
          </div>
        </div>

        <SwitchField
          label="¿Agregar múltiples valores?"
          isTrue="Sí, permitir valores adicionales"
          checked={isArray}
          onChange={(checked) => {
            setIsArray(checked);
            if (!checked) {
              setAllowedQuantityOfValues("");
            }
          }}
          disabled={!(isCreateMode || isNew)}
          required
        />

        <Renderify condition={isArray}>
          <div>
            <TextField
              label="Cantidad máxima de items"
              placeholder="Escribe un número"
              ref={allowedQuantityOfValuesMask}
              value={allowedQuantityOfValues}
              onChange={(e) => setAllowedQuantityOfValues(e.target.value || "")}
              disabled={!(isCreateMode || isNew)}
            />
            <InfoMessage>
              Si se deja vacío, no se aplicará límite de items
            </InfoMessage>
          </div>
        </Renderify>
      </div>
    </ScrollModal>
  );
};
